import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Clock, Calendar, Users, Eye, FileText, BarChart3, Target } from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { MeetingScheduler } from '@/components/meetings/MeetingScheduler';
import { MeetingList } from '@/components/meetings/MeetingList';
import { MeetingTimer } from '@/components/meetings/MeetingTimer';
import { MentoringReport } from '@/components/meetings/MentoringReport';
import { MeetingKPITracker } from '@/components/meetings/MeetingKPITracker';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface MeetingStats {
  total_meetings: number;
  scheduled_meetings: number;
  completed_meetings: number;
  total_mentoring_minutes: number;
  this_month_minutes: number;
  avg_duration: number;
}

export default function MeetingTracker() {
  const { user } = useAuth();
  const { isAdmin, isCoordinator, isPrincipal } = useRole();
  const [activeTab, setActiveTab] = useState('meetings');
  const [showScheduler, setShowScheduler] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [stats, setStats] = useState<MeetingStats>({
    total_meetings: 0,
    scheduled_meetings: 0,
    completed_meetings: 0,
    total_mentoring_minutes: 0,
    this_month_minutes: 0,
    avg_duration: 0
  });
  const [loading, setLoading] = useState(true);

  const canScheduleMeetings = isAdmin || isCoordinator || isPrincipal;

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      // Get basic meeting counts
      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('*')
        .or(`observer_id.eq.${user?.id},observee_id.eq.${user?.id}`);

      if (meetingsError) throw meetingsError;

      const totalMeetings = meetings?.length || 0;
      const scheduledMeetings = meetings?.filter(m => m.status === 'scheduled').length || 0;
      const completedMeetings = meetings?.filter(m => m.status === 'completed').length || 0;
      
      // Calculate mentoring minutes
      const mentoringMeetings = meetings?.filter(m => 
        (m.meeting_type === 'mentoring' || m.meeting_type === 'coaching') && 
        m.status === 'completed' && 
        m.duration_minutes
      ) || [];
      
      const totalMentoringMinutes = mentoringMeetings.reduce((sum, m) => sum + (m.duration_minutes || 0), 0);
      
      // This month's mentoring minutes
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);
      
      const thisMonthMeetings = mentoringMeetings.filter(m => 
        new Date(m.scheduled_date) >= thisMonth
      );
      const thisMonthMinutes = thisMonthMeetings.reduce((sum, m) => sum + (m.duration_minutes || 0), 0);
      
      const avgDuration = completedMeetings > 0 
        ? meetings?.filter(m => m.status === 'completed' && m.duration_minutes)
            .reduce((sum, m) => sum + (m.duration_minutes || 0), 0) / completedMeetings
        : 0;

      setStats({
        total_meetings: totalMeetings,
        scheduled_meetings: scheduledMeetings,
        completed_meetings: completedMeetings,
        total_mentoring_minutes: totalMentoringMinutes,
        this_month_minutes: thisMonthMinutes,
        avg_duration: Math.round(avgDuration)
      });
    } catch (error) {
      console.error('Error loading meeting stats:', error);
      toast.error('Failed to load meeting statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMeeting = () => {
    setShowScheduler(true);
  };

  const handleMeetingScheduled = () => {
    setShowScheduler(false);
    loadStats();
    toast.success('Meeting scheduled successfully');
  };

  const handleStartMeeting = (meeting: any) => {
    setActiveMeeting(meeting);
    setActiveTab('timer');
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Meeting Tracker</h1>
          <p className="text-muted-foreground mt-2">
            Track observation meetings, mentoring sessions, and calculate professional development minutes.
          </p>
        </div>
        
        {canScheduleMeetings && (
          <Button onClick={handleScheduleMeeting} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Schedule Meeting
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_meetings}</div>
            <p className="text-xs text-muted-foreground">
              {stats.scheduled_meetings} scheduled
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed_meetings}</div>
            <p className="text-xs text-muted-foreground">
              Avg {stats.avg_duration}min each
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mentoring</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutes(stats.total_mentoring_minutes)}
            </div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutes(stats.this_month_minutes)}
            </div>
            <p className="text-xs text-muted-foreground">Mentoring minutes</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="meetings" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Meetings
          </TabsTrigger>
          <TabsTrigger value="timer" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Timer
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="kpis" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            KPIs
          </TabsTrigger>
          <TabsTrigger value="minutes" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Minutes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="meetings" className="space-y-4">
          <MeetingList 
            onStartMeeting={handleStartMeeting}
            onRefresh={loadStats}
            canSchedule={canScheduleMeetings}
          />
        </TabsContent>

        <TabsContent value="timer" className="space-y-4">
          <MeetingTimer 
            meeting={activeMeeting}
            onMeetingComplete={loadStats}
          />
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <MentoringReport />
        </TabsContent>

        <TabsContent value="kpis" className="space-y-4">
          <MeetingKPITracker selectedPeriod="current" />
        </TabsContent>

        <TabsContent value="minutes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meeting Minutes & Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                View and manage meeting minutes from completed observations and mentoring sessions.
              </p>
              {/* Meeting minutes component would go here */}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showScheduler && (
        <MeetingScheduler
          onClose={() => setShowScheduler(false)}
          onScheduled={handleMeetingScheduled}
        />
      )}
    </div>
  );
}